import type { TutorialStep } from "./types.ts";

/**
 * 「ダッシュボードのカスタマイズ」チュートリアルのステップ定義（PC 版限定）。
 *
 * 大きく 3 部構成:
 *   第1部  ダッシュボードの基本的な配置（上部のスコアカード・下部のテーブル）を知る。
 *   第2部  スコアカードのカスタマイズを体験する。「共通（全員）」と「個人（自分だけ）」で
 *          別々に保存できることを学ぶ。
 *   第3部  テーブルの列表示設定のカスタマイズを体験する。こちらも「共通」「個人」で
 *          別々に保存できる。
 *
 * ダッシュボードはウィンドウ幅の広い PC 版でのみ利用できる機能のため、
 * HelpDrawer 側で isMobile のときはこのチュートリアルを表示しない。
 * targetKey は DashboardSandboxScreen 側の data-tut 属性と対応する。
 */
export const DASHBOARD_TUTORIAL_STEPS: TutorialStep[] = [
  // ===== 第1部: 基本的な配置 =====
  {
    id: "welcome",
    targetKey: null,
    placement: "center",
    title: "ダッシュボードのカスタマイズを学ぶ",
    body:
      "ダッシュボードは、プロジェクトのタスクを「集計」と「一覧」で俯瞰できるビューです。画面が広い PC 版でのみ利用できます。\n" +
      "まず基本的な配置（スコアカードとテーブル）を確認し、そのあとスコアカードと列表示のカスタマイズを体験します。\n" +
      "カスタマイズは「共通（全員に適用）」と「個人（自分だけに適用）」の2種類を、それぞれ別に保存できます。",
  },
  {
    id: "layout-scorecards",
    targetKey: "dash-scorecards",
    placement: "bottom",
    title: "① 上部：スコアカード",
    body:
      "画面上部には「スコアカード」が並びます。タスクを期限・ステータスなどのカテゴリで集計し、件数をひと目で把握できます。\n" +
      "カードをクリックすると、その条件でテーブルを絞り込めます。",
  },
  {
    id: "layout-table",
    targetKey: "dash-table",
    placement: "top",
    title: "② 下部：テーブル",
    body:
      "画面下部には、タスクの一覧が「テーブル」で表示されます。ステータス・担当者・期限・重要度・タグなどを列として横並びで確認・編集できます。\n" +
      "このスコアカードとテーブルの2つが、ダッシュボードの基本的な配置です。",
  },

  // ===== 第2部: スコアカードのカスタマイズ =====
  {
    id: "scorecard-mode",
    targetKey: "dash-scorecard-mode",
    placement: "bottom",
    title: "③ スコアカードの共通／個人",
    body:
      "スコアカードの表示は「共通」と「個人」で切り替えられます。\n" +
      "「共通」はプロジェクトのメンバー全員に適用される設定、「個人」は自分だけに適用される設定です。2つは別々に保存されます。\n" +
      "まずは「共通」の状態でカスタマイズしてみましょう。",
  },
  {
    id: "scorecard-edit",
    targetKey: "dash-edit-btn",
    placement: "bottom",
    title: "④ 「表示を編集」を押す",
    body: "スコアカードの右上にある「表示を編集」ボタンを押して、編集モードに入りましょう。",
    expectAction: "dash-scorecard-edit",
  },
  {
    id: "scorecard-explain-edit",
    targetKey: "dash-scorecards",
    placement: "bottom",
    title: "編集モードに入りました",
    body:
      "編集モードでは、いま表示している「期限」カテゴリのカードを増やしたり、集計するカテゴリそのものを追加したりできます。\n" +
      "まずは期限のカードをカスタマイズしてみましょう。次へ進んでください。",
  },
  {
    id: "scorecard-add-deadline-card",
    targetKey: "dash-add-deadline-card",
    placement: "bottom",
    title: "⑤ 期限カードを追加する",
    body:
      "「期限」カテゴリでは、右端の「カードを追加」で『期限まで〇～〇日』のカードを自由に増やせます。\n" +
      "押して、新しい期限カードを1枚追加してみましょう。",
    expectAction: "dash-scorecard-add-deadline-card",
  },
  {
    id: "scorecard-edit-deadline-card",
    targetKey: "dash-scorecards",
    placement: "bottom",
    title: "⑥ 追加したカードを編集する",
    body:
      "新しく追加された期限カードの右上に、鉛筆（編集）アイコンが表示されています。\n" +
      "その鉛筆アイコンを押して、カードの編集フォームを開きましょう。",
    expectAction: "dash-scorecard-edit-deadline-card",
  },
  {
    id: "scorecard-confirm-deadline-card",
    targetKey: "dash-scorecards",
    placement: "bottom",
    title: "⑦ 日数と表示名を決めて確定する",
    body:
      "編集フォームで、期限までの日数の範囲（例：3～7日。片方だけの入力もOK）と表示名を入力できます。\n" +
      "入力できたら、オレンジ色のチェックボタンを押して確定しましょう。この範囲内に期限が来るタスクが、このカードに集計されます。",
    expectAction: "dash-scorecard-confirm-deadline-card",
  },
  {
    id: "scorecard-open-add-menu",
    targetKey: "dash-add-category",
    placement: "bottom",
    title: "⑧ カテゴリを追加する",
    body:
      "次は集計する「カテゴリ」を増やしてみます。期限・ステータス以外にも、重要度・タグ・担当者などで集計できます。\n" +
      "タブ右側の「カテゴリを追加」を押して、追加できるカテゴリの一覧を開きましょう。",
    expectAction: "dash-scorecard-open-add-menu",
  },
  {
    id: "scorecard-pick-category",
    targetKey: "dash-add-category-menu",
    placement: "bottom",
    title: "⑨ 追加するカテゴリを選ぶ",
    body:
      "開いた一覧から、追加したいカテゴリ（例：「重要度」）を選んでください。\n" +
      "選ぶと、スコアカードの上部タブにそのカテゴリが追加されます。",
    expectAction: "dash-scorecard-add-category",
  },
  {
    id: "scorecard-save",
    targetKey: "dash-edit-btn",
    placement: "bottom",
    title: "⑩ 「修正を保存」する",
    body:
      "カスタマイズできたら、右上のボタン（「修正を保存」）を押して確定します。\n" +
      "いま編集していたのは「共通」の設定なので、この内容はメンバー全員のダッシュボードに反映されます。",
    expectAction: "dash-scorecard-save",
  },
  {
    id: "scorecard-personal",
    targetKey: "dash-scorecard-mode",
    placement: "bottom",
    title: "⑪ 「個人」に切り替える",
    body:
      "次は「個人」の設定です。切り替えの「個人」を押してみましょう。\n" +
      "個人設定は自分だけに適用され、共通設定とは独立して保存されます。",
    expectAction: "dash-scorecard-personal",
  },
  {
    id: "scorecard-personal-explain",
    targetKey: "dash-scorecards",
    placement: "bottom",
    title: "個人設定も同じ手順でカスタマイズ",
    body:
      "「個人」でも、同じように「表示を編集」から、期限カードの追加・編集やカテゴリの追加ができます。\n" +
      "個人設定は共通設定に影響しません。自分の見やすいようにカードを並べられます。確認できたら「次へ」を押してください。",
  },

  // ===== 第3部: 列表示設定のカスタマイズ =====
  {
    id: "column-mode",
    targetKey: "dash-column-mode",
    placement: "top",
    title: "⑫ 列設定の共通／個人",
    body:
      "テーブルの下には「列設定」があります。テーブルに表示する列や順番も、スコアカードと同じく「共通」「個人」で別々に保存できます。",
  },
  {
    id: "column-open",
    targetKey: "dash-column-settings-btn",
    placement: "top",
    title: "⑬ 「列の表示設定」を開く",
    body: "「列の表示設定」ボタンを押して、列のカスタマイズ画面を開きましょう。",
    expectAction: "dash-open-column-settings",
  },
  {
    id: "column-common",
    targetKey: "dash-column-dialog",
    placement: "left",
    title: "共通タブ：表示と順番を設定",
    body:
      "開いた画面の「共通設定」タブでは、チェックボックスで列の表示／非表示を切り替え、▲▼で並び順を変更できます。\n" +
      "確認できたら「次へ」を押してください。",
  },
  {
    id: "column-custom",
    targetKey: "dash-column-custom",
    placement: "left",
    title: "カスタム項目の列も設定できる",
    body:
      "「全体設定」のカスタム項目で項目（例：「見積工数」）を追加していると、その項目も列として一覧に現れ、「カスタム」バッジが付きます。\n" +
      "標準の列と同じように、表示／非表示や並び順を設定できます。確認できたら「次へ」を押してください。",
  },
  {
    id: "column-personal",
    targetKey: "dash-column-personal-tab",
    placement: "left",
    title: "⑭ 「個人設定」タブに切り替える",
    body:
      "上部の「個人設定」タブをクリックしてください。\n" +
      "こちらは自分だけに適用される列設定です。共通設定とは別に保存されます。",
    expectAction: "dash-column-personal",
  },
  {
    id: "column-save",
    targetKey: "dash-column-save",
    placement: "left",
    title: "⑮ 「保存する」で確定する",
    body: "列の表示や順番を設定できたら、「保存する」を押して確定しましょう。",
    expectAction: "dash-column-save",
  },

  {
    id: "done",
    targetKey: null,
    placement: "center",
    title: "お疲れさまでした！",
    body:
      "ダッシュボードは「スコアカード（集計）」と「テーブル（一覧）」で構成されます。\n" +
      "スコアカードは「表示を編集」から、テーブルの列は「列の表示設定」から、それぞれカスタマイズできます。\n" +
      "どちらも「共通（全員）」と「個人（自分だけ）」で別々に保存できます。「完了」を押すとチュートリアルを終了します。",
  },
];
